import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Minus,
  Plus,
  Ruler,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { BASE_URL } from "../../../config/config";
import axios from "axios";
import { message } from "antd";
import { Buffer } from "buffer";
import {
  Transaction,
  TransactionInstruction,
  PublicKey,
} from "@solana/web3.js";
import {
  connectPhantom,
  disconnectPhantom,
  getPhantomProvider,
  connection,
} from "../../../utils/phantom.js";
import PaymentMethodModal from "../../../components/paymentMethodModal/PaymentMethodModal.jsx";

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "AED 0";
  }

  return new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency: "AED",
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "0";
  }

  return new Intl.NumberFormat("en-AE").format(Number(value));
};


const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  }
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return error?.message || fallback;
};

const PropertyInvestmentDetails = ({ onBack, onBuy }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [shares, setShares] = useState(1);
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] =
  useState(false);
  const [walletAddress, setWalletAddress] = useState("");
  const [walletConnecting, setWalletConnecting] = useState(false);
  const [isBuying, setIsBuying] = useState(false);
  const [transactionSignature, setTransactionSignature] = useState("");
  const [walletInput, setWalletInput] = useState("");
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [purchaseDetails, setPurchaseDetails] = useState(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isPhantomErrorModalOpen, setIsPhantomErrorModalOpen] = useState(false);
  const [phantomErrorMessage, setPhantomErrorMessage] = useState("");
  const [profile, setProfile] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isProfileUpdating, setIsProfileUpdating] = useState(false);
  const [profileError, setProfileError] = useState("");

  // ---- Secondary marketplace state ----
  const [listings, setListings] = useState([]);
  const [isLoadingListings, setIsLoadingListings] = useState(false);
  const [listingsError, setListingsError] = useState(false);
  const [myListingError, setMyListingError] = useState(false);
  const [myListing, setMyListing] = useState(null); // this wallet's own active listing for this property, if any

  const [isListingModalOpen, setIsListingModalOpen] = useState(false);
  const [listingBlocks, setListingBlocks] = useState(1);
  const [listingPrice, setListingPrice] = useState("");
  const [isCreatingListing, setIsCreatingListing] = useState(false);
  const [listingError, setListingError] = useState("");

  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [isCancellingListing, setIsCancellingListing] = useState(false);

  const [isBuyListingModalOpen, setIsBuyListingModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [buyListingBlocks, setBuyListingBlocks] = useState(1);
  const [isBuyingListing, setIsBuyingListing] = useState(false);

  const [isRequestingBuyback, setIsRequestingBuyback] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const ID = location.state?.id;

  const handleConnectWallet = async () => {
    try {
      setWalletConnecting(true);
      setPhantomErrorMessage("");

      const provider = getPhantomProvider();

      if (!provider) {
        throw new Error(
          "Phantom Wallet is not installed. Please install the Phantom browser extension and refresh this page.",
        );
      }

      const response = await provider.connect();

      const publicKey = response?.publicKey || provider.publicKey;

      if (!publicKey) {
        throw new Error(
          "Phantom connected, but no Solana wallet address was returned.",
        );
      }

      const address = publicKey.toString();

      console.log("Phantom connected:", address);

      setWalletAddress(address);
    } catch (error) {
      console.log("Phantom connection error:", error);

      setPhantomErrorMessage(
        error?.code === 4001
          ? "You rejected the Phantom wallet connection."
          : error?.message || "Unable to connect Phantom wallet.",
      );

      setIsPhantomErrorModalOpen(true);
    } finally {
      setWalletConnecting(false);
    }
  };

  const handleDisconnectWallet = async () => {
    try {
      await disconnectPhantom();

      setWalletAddress("");
    } catch (error) {
      console.log("Wallet disconnect error:", error);
    }
  };

  const updateInvestorProfile = async (formData) => {
    setIsProfileUpdating(true);
    setProfileError("");
    const token = localStorage.getItem("rwatoken");

    try {
      const response = await axios.patch(`${BASE_URL}/investor/me`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Profile updated:", response.data);
      message.success("Profile Updated SuccessFully");
      setIsProfileModalOpen(false);
      setIsWalletModalOpen(false);
      // Refresh profile after successful update
      await getProfile();
    } catch (error) {
      console.log("Complete registration error:", error);
      message.error(
        getErrorMessage(error, "Something went wrong. Please try again."),
      );
    } finally {
      setIsProfileUpdating(false);
    }
  };

  const handleBuyClick = () => {
    const savedWalletAddress =
      profile?.wallet_address || profile?.walletAddress || "";

    if (!savedWalletAddress.trim()) {
      setProfileError("");
      setIsProfileModalOpen(true);
      return;
    }

    // Wallet already exists
    setIsPaymentMethodModalOpen(true)
    // setWalletInput(savedWalletAddress);
    // setIsWalletModalOpen(true);
  };

  const getProfile = async () => {
    try {
      const token = localStorage.getItem("rwatoken");
      const response = await axios.get(`${BASE_URL}/investor/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.data) {
        setProfile(response?.data);
      }
    } catch (error) {
      console.log("Error fetching investor profile:", error);

      setProfile(null);
    } finally {
    }
  };

  const approveAndSendPayment = async (purchaseData) => {
    const provider = getPhantomProvider();

    if (!provider) {
      throw new Error(
        "Phantom Wallet is not available. Please install/open Phantom and connect it.",
      );
    }

    if (!provider.publicKey) {
      throw new Error(
        "Phantom is not connected. Please connect your Phantom wallet first.",
      );
    }

    setIsBuying(true);
    setPhantomErrorMessage("");

    try {
      /*
       * The backend purchase API prepares the Solana program instruction.
       *
       * Expected response:
       * {
       *   id: 9,
       *   blocks_purchased: 1,
       *   price_paid_aed: 100,
       *   instruction: {
       *     programId: "...",
       *     keys: [...],
       *     data: "base64..."
       *   }
       * }
       *
       * tx_hash in the current API is actually a JSON string containing
       * the same instruction. It is NOT a real Solana transaction signature.
       */
      let rawInstruction = purchaseData?.instruction;

      if (!rawInstruction && purchaseData?.tx_hash) {
        try {
          rawInstruction =
            typeof purchaseData.tx_hash === "string"
              ? JSON.parse(purchaseData.tx_hash)
              : purchaseData.tx_hash;
        } catch {
          throw new Error(
            "The server returned an invalid tx_hash/instruction payload.",
          );
        }
      }

      if (
        !rawInstruction?.programId ||
        !Array.isArray(rawInstruction?.keys) ||
        !rawInstruction?.data
      ) {
        throw new Error(
          "The purchase API did not return a valid Solana instruction.",
        );
      }

      const phantomPublicKey = provider.publicKey;
      const phantomAddress = phantomPublicKey.toString();

      /*
       * IMPORTANT:
       * The backend instruction must be prepared for THIS wallet.
       *
       * At least one signer should normally be the connected Phantom
       * wallet. We validate that before opening Phantom.
       */
      const signerKeys = rawInstruction.keys.filter((key) => key?.isSigner);

      if (signerKeys.length === 0) {
        throw new Error(
          "The Solana purchase instruction has no signer account. The backend must include the buyer wallet as a signer.",
        );
      }

      const walletIsSigner = signerKeys.some(
        (key) => key.pubkey === phantomAddress,
      );

      if (!walletIsSigner) {
        throw new Error(
          `Wallet mismatch. Connected Phantom wallet is ${phantomAddress}, but the backend instruction expects signer(s): ${signerKeys
            .map((key) => key.pubkey)
            .join(", ")}`,
        );
      }

      /*
       * Convert the backend instruction into a real Solana
       * TransactionInstruction.
       */
      const purchaseInstruction = new TransactionInstruction({
        programId: new PublicKey(rawInstruction.programId),
        keys: rawInstruction.keys.map((key) => ({
          pubkey: new PublicKey(key.pubkey),
          isSigner: Boolean(key.isSigner),
          isWritable: Boolean(key.isWritable),
        })),
        data: Buffer.from(rawInstruction.data, "base64"),
      });

      /*
       * Build the transaction.
       *
       * The backend gives us the program instruction.
       * The frontend supplies transaction-level information:
       * - fee payer
       * - recent blockhash
       */
      const transaction = new Transaction();

      /*
       * Optional:
       * If the backend returns initialization instructions in the future,
       * they can be placed before the Buy instruction.
       *
       * Example:
       * purchaseData.pre_instructions = [...]
       */
      if (Array.isArray(purchaseData?.pre_instructions)) {
        purchaseData.pre_instructions.forEach((item) => {
          if (!item?.programId || !Array.isArray(item?.keys) || !item?.data) {
            return;
          }

          transaction.add(
            new TransactionInstruction({
              programId: new PublicKey(item.programId),
              keys: item.keys.map((key) => ({
                pubkey: new PublicKey(key.pubkey),
                isSigner: Boolean(key.isSigner),
                isWritable: Boolean(key.isWritable),
              })),
              data: Buffer.from(item.data, "base64"),
            }),
          );
        });
      }

      transaction.add(purchaseInstruction);

      transaction.feePayer = phantomPublicKey;

      const latestBlockhash = await connection.getLatestBlockhash("confirmed");

      transaction.recentBlockhash = latestBlockhash.blockhash;

      const result = await provider.signAndSendTransaction(transaction);

      const signature = typeof result === "string" ? result : result?.signature;

      if (!signature) {
        throw new Error("Phantom returned no transaction signature.");
      }

      await connection.confirmTransaction(
        {
          signature,
          blockhash: latestBlockhash.blockhash,
          lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
        },
        "confirmed",
      );

      // setTransactionSignature(signature);
      // setIsWalletModalOpen(false);
      // setIsSuccessModalOpen(true);

      /*
       * Refresh the property after a successful on-chain transaction.
       */
      await getPurchaseConfirmation(signature, purchaseData?.id);
      // await viewDetails();

      return signature;
    } catch (error) {
      console.log("Phantom transaction error:", error);

      if (error?.code === 4001) {
        throw new Error("You rejected the transaction in Phantom.");
      }

      throw new Error(
        error?.message || "The Solana transaction could not be completed.",
      );
    } finally {
      setIsBuying(false);
    }
  };

  const getPurchaseConfirmation = async (sign, id) => {
    if (!id || !sign) {
      setIsWalletModalOpen(false);
      message.error("Something Went Wrong. Please try Again.");
      return;
    }
    const token = localStorage.getItem("rwatoken");
    const payload = {
      signature: sign,
    };
    try {
      const response = await axios.post(
        `${BASE_URL}/investor/me/purchases/${id}/confirm`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      setTransactionSignature(sign);
      setIsWalletModalOpen(false);
      setIsSuccessModalOpen(true);
      setPurchaseDetails(response.data);
      await viewDetails();
    } catch (error) {
      console.log(error, "Error");
      // message.error(
      //   error?.response?.detail ||
      //     "Something Went Wrong. Please try Again."
      // );
      throw new Error(
        getErrorMessage(error, "Something Went Wrong. Please try Again."),
      );
    } finally {
    }
  };

  useEffect(() => {
    const provider = getPhantomProvider();

    if (!provider) return;

    if (provider.publicKey) {
      setWalletAddress(provider.publicKey.toString());
    }

    const handleAccountChanged = (publicKey) => {
      if (publicKey) {
        setWalletAddress(publicKey.toString());
      } else {
        setWalletAddress("");
      }
    };

    const handleDisconnect = () => {
      setWalletAddress("");
    };

    provider.on("accountChanged", handleAccountChanged);
    provider.on("disconnect", handleDisconnect);

    return () => {
      provider.removeListener("accountChanged", handleAccountChanged);

      provider.removeListener("disconnect", handleDisconnect);
    };
  }, []);

  const viewDetails = async () => {
    if (!ID) return;

    try {
      setIsLoading(true);

      const token = localStorage.getItem("rwatoken");

      const response = await axios.get(
        `${BASE_URL}/investor/properties/${ID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response?.data) {
        setSelectedProperty(response.data);
        setSelectedImage(0);
      }
    } catch (error) {
      console.log("Error fetching property details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    viewDetails();
    getProfile();
  }, [ID]);

  /* ---------------- Property Mapping ---------------- */

  const property = useMemo(() => {
    if (!selectedProperty) {
      return null;
    }

    const totalBlocks = Number(selectedProperty?.total_tokens || 0);
    const blocksSold = Number(selectedProperty?.blocks_sold || 0);

    const availableBlocks = Math.max(totalBlocks - blocksSold, 0);

    /*
      Convert API image paths into complete URLs.

      API:
      property_images/45d005ace67d45949a6537dc7e4a024e.png

      Final:
      http://18.145.218.203:8000/uploads/property_images/...
    */
    const images =
      selectedProperty?.image_paths?.length > 0
        ? selectedProperty.image_paths.map(
            (image) => `${BASE_URL}/uploads/${image}`,
          )
        : [];

    return {
      id: selectedProperty?.id,

      name: selectedProperty?.name,

      description: selectedProperty?.description,

      location: selectedProperty?.location,

      type: selectedProperty?.property_type,

      category: selectedProperty?.property_type,

      images,

      propertyValue: Number(selectedProperty?.property_value_aed || 0),

      sharePrice: Number(selectedProperty?.price_per_block_aed || 0),

      totalShares: totalBlocks,

      availableShares: availableBlocks,

      blocksSold: blocksSold,

      roi: Number(selectedProperty?.gross_annual_yield_pct || 0),

      area: selectedProperty?.area_sqft,

      fundingPercentage: Number(selectedProperty?.funding_pct || 0),

      vestingDays: Number(selectedProperty?.vesting_days || 0),

      status: selectedProperty?.is_published ? "PUBLISHED" : "UNPUBLISHED",

      submissionStatus: selectedProperty?.submission_status,

      rejectionReason: selectedProperty?.rejection_reason,

      tokenAddress: selectedProperty?.token_address,
    };
  }, [selectedProperty]);

  /* ---------------- Images ---------------- */

  const images = property?.images?.length ? property.images : [];

  /* ---------------- Calculations ---------------- */

  const totalInvestment = useMemo(() => {
    return shares * Number(property?.sharePrice || 0);
  }, [shares, property?.sharePrice]);

  const fundedPercentage = useMemo(() => {
    if (!property?.totalShares) {
      return 0;
    }

    /*
      Prefer API funding_pct when available.
      Otherwise calculate from blocks sold.
    */
    if (
      selectedProperty?.funding_pct !== null &&
      selectedProperty?.funding_pct !== undefined
    ) {
      return Number(selectedProperty.funding_pct);
    }

    return (
      (Number(property?.blocksSold || 0) / Number(property?.totalShares || 1)) *
      100
    );
  }, [
    property?.totalShares,
    property?.blocksSold,
    selectedProperty?.funding_pct,
  ]);

  /*
    Ownership percentage for one token/block.

    Example:
    10,000 total blocks
    1 block = 0.01%
  */
  const ownershipPerShare = useMemo(() => {
    if (!property?.totalShares) {
      return "0%";
    }

    const percentage = 100 / Number(property.totalShares);

    return `${percentage.toFixed(2)}%`;
  }, [property?.totalShares]);

  /* ---------------- Share Controls ---------------- */

  const increaseShares = () => {
    if (shares < Number(property?.availableShares || 0)) {
      setShares((prev) => prev + 1);
    }
  };

  const decreaseShares = () => {
    if (shares > 1) {
      setShares((prev) => prev - 1);
    }
  };

  const handleConfirmPurchase = async () => {
    const provider = getPhantomProvider();

    if (!provider) {
      setPhantomErrorMessage(
        "Phantom Wallet is not installed. Please install the Phantom browser extension.",
      );
      setIsPhantomErrorModalOpen(true);
      return;
    }

    if (!provider.publicKey) {
      setPhantomErrorMessage(
        "Please connect your Phantom wallet before confirming the purchase.",
      );
      setIsPhantomErrorModalOpen(true);
      return;
    }

    setWalletAddress(provider.publicKey.toString());
    setIsSubmitting(true);
    setPhantomErrorMessage("");

    const token = localStorage.getItem("rwatoken");

    try {
      const payload = {
        blocks: shares,
        wallet_address: provider.publicKey.toString(),
      };

      const response = await axios.post(
        `${BASE_URL}/investor/me/properties/${property?.id}/purchase`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response?.data) {
        throw new Error("Purchase API returned an empty response.");
      }

      // setPurchaseDetails(response.data);

      /*
       * IMPORTANT:
       * The purchase endpoint prepares the transaction.
       * approveAndSendPayment() is what sends that prepared
       * transaction to Phantom for user approval.
       */
      await approveAndSendPayment(response.data);
    } catch (error) {
      console.log("Purchase / Phantom error:", error);

      setPhantomErrorMessage(
        getErrorMessage(error, "The purchase transaction could not be completed."),
      );

      setIsPhantomErrorModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };


  const signAndSendMarketplaceInstruction = async (rawInstruction) => {
    const provider = getPhantomProvider();

    if (!provider || !provider.publicKey) {
      throw new Error("Please connect your Phantom wallet first.");
    }

    if (
      !rawInstruction?.programId ||
      !Array.isArray(rawInstruction?.keys) ||
      !rawInstruction?.data
    ) {
      throw new Error("The server did not return a valid Solana instruction.");
    }

    const phantomPublicKey = provider.publicKey;

    const ix = new TransactionInstruction({
      programId: new PublicKey(rawInstruction.programId),
      keys: rawInstruction.keys.map((key) => ({
        pubkey: new PublicKey(key.pubkey),
        isSigner: Boolean(key.isSigner),
        isWritable: Boolean(key.isWritable),
      })),
      data: Buffer.from(rawInstruction.data, "base64"),
    });

    const transaction = new Transaction();
    transaction.add(ix);
    transaction.feePayer = phantomPublicKey;

    const latestBlockhash = await connection.getLatestBlockhash("confirmed");
    transaction.recentBlockhash = latestBlockhash.blockhash;

    const result = await provider.signAndSendTransaction(transaction);
    const signature = typeof result === "string" ? result : result?.signature;

    if (!signature) {
      throw new Error("Phantom returned no transaction signature.");
    }

    const confirmResult = await connection.confirmTransaction(
      {
        signature,
        blockhash: latestBlockhash.blockhash,
        lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
      },
      "confirmed",
    );

    const onChainError = confirmResult?.value?.err;
    if (onChainError) {
     
      const customCode = onChainError?.InstructionError?.[1]?.Custom;
      if (customCode === 6013) {
        throw new Error(
          "These tokens are still within their vesting lock-up period and can't be listed, sold, or transferred yet.",
        );
      }
      throw new Error(
        `Transaction was rejected on-chain: ${JSON.stringify(onChainError)}`,
      );
    }

    return signature;
  };

  const listingsRequestRef = useRef(0);
  const myListingRequestRef = useRef(0);

  const fetchListings = async () => {
    if (!property?.id) return;
    const requestId = ++listingsRequestRef.current;
    setIsLoadingListings(true);
    const token = localStorage.getItem("rwatoken");
    try {
      const response = await axios.get(
        `${BASE_URL}/investor/secondary/properties/${property.id}/listings`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      
      const data = response?.data;
      setListings(Array.isArray(data) ? data : data?.listings || []);
      setListingsError(false);
    } catch (error) {
      console.log("Error fetching listings:", error);
      if (requestId === listingsRequestRef.current) {
        setListings([]);
        setListingsError(true);
      }
    } finally {
      if (requestId === listingsRequestRef.current) setIsLoadingListings(false);
    }
  };

  const fetchMyListing = async () => {
    if (!property?.id || !walletAddress) {
      setMyListing(null);
      return;
    }
    const requestId = ++myListingRequestRef.current;
    const token = localStorage.getItem("rwatoken");
    try {
      const response = await axios.get(
        `${BASE_URL}/investor/secondary/properties/${property.id}/listings/${walletAddress}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (requestId !== myListingRequestRef.current) return;
      const data = response?.data;
      const rawListing = data?.listing !== undefined ? data.listing : data;
      const normalizedListing = rawListing
        ? {
            ...rawListing,
            id: rawListing.id ?? rawListing.listing_id ?? rawListing.listingId,
          }
        : null;
      setMyListing(normalizedListing);
      setMyListingError(false);
    } catch (error) {
      console.log("Error fetching your listing:", error);
      if (requestId === myListingRequestRef.current) {
        setMyListing(null);
        setMyListingError(true);
      }
    }
  };

  useEffect(() => {
    fetchListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.id]);

  useEffect(() => {
    fetchMyListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.id, walletAddress]);

  const openListingModal = async () => {
    let currentWallet = walletAddress;
    if (!currentWallet) {
      const provider = getPhantomProvider();
      if (!provider) {
        setPhantomErrorMessage(
          "Phantom Wallet is not installed. Please install the Phantom browser extension.",
        );
        setIsPhantomErrorModalOpen(true);
        return;
      }
      try {
        setWalletConnecting(true);
        const response = await provider.connect();
        const publicKey = response?.publicKey || provider.publicKey;
        if (!publicKey) {
          throw new Error("Phantom connected, but no wallet address was returned.");
        }
        currentWallet = publicKey.toString();
        setWalletAddress(currentWallet);
      } catch (error) {
        setPhantomErrorMessage(
          error?.code === 4001
            ? "You rejected the Phantom wallet connection."
            : error?.message || "Unable to connect Phantom wallet.",
        );
        setIsPhantomErrorModalOpen(true);
        return;
      } finally {
        setWalletConnecting(false);
      }
    }
    setListingBlocks(1);
    setListingPrice(property?.sharePrice ? String(property.sharePrice) : "");
    setListingError("");
    setIsListingModalOpen(true);
  };

  const handleCreateListing = async () => {
    if (!listingBlocks || Number(listingBlocks) <= 0) {
      setListingError("Enter how many tokens you want to list.");
      return;
    }
    if (!listingPrice || Number(listingPrice) <= 0) {
      setListingError("Enter a price per token.");
      return;
    }

    setIsCreatingListing(true);
    setListingError("");
    const token = localStorage.getItem("rwatoken");

    try {
      const response = await axios.post(
        `${BASE_URL}/investor/secondary/me/properties/${property.id}/listings`,
        {
          quantity: Number(listingBlocks),
          price_per_token_aed: Number(listingPrice),
          wallet_address: walletAddress,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const listingId =
        response?.data?.id ??
        response?.data?.listing_id ??
        response?.data?.listingId ??
        response?.data?.listing?.id;
      const signature = await signAndSendMarketplaceInstruction(
        response?.data?.instruction,
      );

      await axios.post(
        `${BASE_URL}/investor/secondary/me/confirm`,
        { type: "listing", id: listingId, signature },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      message.success("Your listing is live.");
      setIsListingModalOpen(false);
      await fetchListings();
      await fetchMyListing();
    } catch (error) {
      console.log("Create listing error:", error);
      const message = getErrorMessage(error, "Could not create your listing. Please try again.");
      if (message.toLowerCase().includes("already have an active listing")) {
        setIsListingModalOpen(false);
        await fetchMyListing();
        return;
      }
      setListingError(error?.code === 4001 ? "You rejected the transaction in Phantom." : message);
    } finally {
      setIsCreatingListing(false);
    }
  };

  const handleCancelListing = async () => {
    if (!myListing?.id) {
      message.error(
        "Couldn't find your listing's ID — try refreshing the page before cancelling.",
      );
      return;
    }
    setIsCancellingListing(true);
    const token = localStorage.getItem("rwatoken");

    try {
      const response = await axios.post(
        `${BASE_URL}/investor/secondary/me/listings/${myListing.id}/cancel`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const signature = await signAndSendMarketplaceInstruction(
        response?.data?.instruction,
      );

      await axios.post(
        `${BASE_URL}/investor/secondary/me/confirm`,
        { type: "listing", id: myListing.id, signature },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      message.success("Listing cancelled.");
      setIsCancelConfirmOpen(false);
      await fetchListings();
      await fetchMyListing();
    } catch (error) {
      console.log("Cancel listing error:", error);
      message.error(
        error?.code === 4001
          ? "You rejected the transaction in Phantom."
          : getErrorMessage(error, "Could not cancel your listing."),
      );
    } finally {
      setIsCancellingListing(false);
    }
  };

  const openBuyListingModal = async (listing) => {
    let currentWallet = walletAddress;
    if (!currentWallet) {
      const provider = getPhantomProvider();
      if (!provider) {
        setPhantomErrorMessage(
          "Phantom Wallet is not installed. Please install the Phantom browser extension.",
        );
        setIsPhantomErrorModalOpen(true);
        return;
      }
      try {
        setWalletConnecting(true);
        const response = await provider.connect();
        const publicKey = response?.publicKey || provider.publicKey;
        if (!publicKey) {
          throw new Error("Phantom connected, but no wallet address was returned.");
        }
        currentWallet = publicKey.toString();
        setWalletAddress(currentWallet);
      } catch (error) {
        setPhantomErrorMessage(
          error?.code === 4001
            ? "You rejected the Phantom wallet connection."
            : error?.message || "Unable to connect Phantom wallet.",
        );
        setIsPhantomErrorModalOpen(true);
        return;
      } finally {
        setWalletConnecting(false);
      }
    }
    setSelectedListing({
      ...listing,
      id: listing?.id ?? listing?.listing_id ?? listing?.listingId,
    });
    setBuyListingBlocks(1);
    setIsBuyListingModalOpen(true);
  };

  const handleBuyFromListing = async () => {
    if (!selectedListing?.id) {
      message.error(
        "Couldn't find this listing's ID — try closing this and reopening it.",
      );
      return;
    }
    setIsBuyingListing(true);
    const token = localStorage.getItem("rwatoken");

    try {
      const response = await axios.post(
        `${BASE_URL}/investor/secondary/me/listings/${selectedListing.id}/fulfill`,
        {
          quantity: Number(buyListingBlocks),
          wallet_address: walletAddress,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const tradeId = response?.data?.id;
      const signature = await signAndSendMarketplaceInstruction(
        response?.data?.instruction,
      );

      await axios.post(
        `${BASE_URL}/investor/secondary/me/confirm`,
        { type: "trade", id: tradeId, signature },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      message.success("Purchase from listing confirmed.");
      setIsBuyListingModalOpen(false);
      setSelectedListing(null);
      await fetchListings();
      await viewDetails();
    } catch (error) {
      console.log("Fulfill listing error:", error);
      message.error(
        error?.code === 4001
          ? "You rejected the transaction in Phantom."
          : getErrorMessage(error, "Could not complete the purchase."),
      );
    } finally {
      setIsBuyingListing(false);
    }
  };

  const handleRequestBuyback = async () => {
    if (!myListing?.id) {
      message.error(
        "Couldn't find your listing's ID — try refreshing the page before requesting a buyback.",
      );
      return;
    }
    setIsRequestingBuyback(true);
    const token = localStorage.getItem("rwatoken");

    try {
      await axios.post(
        `${BASE_URL}/investor/secondary/me/listings/${myListing.id}/request-buyback`,
        { quantity: myListing?.blocks ?? myListing?.quantity },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      message.success(
        "Buyback requested — the treasury will review and execute it.",
      );
      await fetchMyListing();
    } catch (error) {
      console.log("Request buyback error:", error);
      message.error(
        getErrorMessage(error, "Could not submit the buyback request."),
      );
    } finally {
      setIsRequestingBuyback(false);
    }
  };

  /* ---------------- Loading ---------------- */

  if (isLoading) {
    return (
      <div className="property-details-page fixed inset-0 z-[9999] flex min-h-screen items-center justify-center overflow-hidden font-['Inter']">
        {/* ================= BLURRED BACKGROUND ================= */}

        <div className="absolute inset-0 bg-white/55 backdrop-blur-2xl" />

        {/* Soft background glow */}

        <div
          className="
          absolute
          -left-32
          top-10
          h-[420px]
          w-[420px]
          rounded-full
          bg-[#e3ecd6]/70
          blur-[110px]
        "
        />

        <div
          className="
          absolute
          -right-32
          bottom-0
          h-[480px]
          w-[480px]
          rounded-full
          bg-[#e8ecd9]/80
          blur-[120px]
        "
        />

        {/* ================= LOADING CONTENT ================= */}

        <div
          className="
          relative
          z-10
          w-[calc(100%-32px)]
          max-w-[390px]
          animate-[loaderCardIn_.6s_cubic-bezier(.22,1,.36,1)]
        "
        >
          <div
            className="
            rounded-[28px]
            border
            border-white/90
            bg-white/80
            px-7
            py-8
            text-center
            shadow-[0_30px_90px_rgba(76,122,46,0.16)]
            backdrop-blur-2xl
          "
          >
            {/* ================= ICON ================= */}

            <div
              className="
              mx-auto
              flex
              h-[62px]
              w-[62px]
              items-center
              justify-center
              rounded-[20px]
              border
              border-[#dfe8cf]
              bg-gradient-to-br
              from-[#fcfaf3]
              to-[#eef2e4]
              text-[#4c7a2e]
              shadow-[0_12px_28px_rgba(76,122,46,0.10)]
            "
            >
              <Building2 size={27} strokeWidth={1.7} />
            </div>

            {/* ================= LOADER ================= */}

            <div className="relative mx-auto mt-7 h-[54px] w-[54px]">
              {/* Outer ring */}

              <div
                className="
                absolute
                inset-0
                rounded-full
                border-[3px]
                border-[#e1e6d7]
              "
              />

              {/* Animated ring */}

              <div
                className="
                absolute
                inset-0
                animate-spin
                rounded-full
                border-[3px]
                border-transparent
                border-t-[#4c7a2e]
                border-r-[#6fa142]
              "
              />

              {/* Center */}

              <div
                className="
                absolute
                inset-[11px]
                flex
                items-center
                justify-center
                rounded-full
                bg-white
                shadow-inner
              "
              >
                <span
                  className="
                  h-2
                  w-2
                  animate-pulse
                  rounded-full
                  bg-[#4c7a2e]
                "
                />
              </div>
            </div>

            {/* ================= TEXT ================= */}

            <div className="mt-6">
              <h3
                className="
                text-[18px]
                font-bold
                tracking-[-0.025em]
                text-[#1f2318]
              "
              >
                Loading property details
              </h3>

              <p
                className="
                mx-auto
                mt-2
                max-w-[270px]
                text-[12px]
                font-medium
                leading-[1.7]
                text-[#6f765f]
              "
              >
                Preparing the property information and investment details for
                you.
              </p>
            </div>

            {/* ================= PROGRESS ================= */}

            <div className="mt-7">
              <div
                className="
                h-[5px]
                w-full
                overflow-hidden
                rounded-full
                bg-[#e8eadf]
              "
              >
                <div
                  className="
                  h-full
                  w-[65%]
                  animate-[loadingProgress_1.8s_ease-in-out_infinite]
                  rounded-full
                  bg-gradient-to-r
                  from-[#4c7a2e]
                  via-[#4c7a2e]
                  to-[#6fa142]
                "
                />
              </div>

              <div className="mt-3 flex items-center justify-center gap-1.5">
                <span className="text-[9px] font-medium text-[#929681]">
                  Fetching property data
                </span>

                <span className="flex gap-1">
                  <span className="h-1 w-1 animate-bounce rounded-full bg-[#4c7a2e]" />
                  <span
                    className="h-1 w-1 animate-bounce rounded-full bg-[#4c7a2e]"
                    style={{ animationDelay: "120ms" }}
                  />
                  <span
                    className="h-1 w-1 animate-bounce rounded-full bg-[#4c7a2e]"
                    style={{ animationDelay: "240ms" }}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ================= ANIMATIONS ================= */}

        <style>{`
        @keyframes loaderCardIn {
          0% {
            opacity: 0;
            transform: translateY(20px) scale(0.96);
            filter: blur(5px);
          }

          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }

        @keyframes loadingProgress {
          0% {
            transform: translateX(-100%);
            width: 35%;
          }

          50% {
            transform: translateX(20%);
            width: 65%;
          }

          100% {
            transform: translateX(160%);
            width: 35%;
          }
        }
      `}</style>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="property-details-page flex min-h-screen items-center justify-center font-['Inter']">
        <div className="text-center">
          <Building2 size={35} className="mx-auto text-[#375c20]" />

          <h2 className="mt-4 text-[22px] font-bold text-[#1f2318]">
            Property not found
          </h2>

          <p className="mt-2 text-[13px] text-[#6f765f]">
            We could not load the property details.
          </p>

          <button
            onClick={() => navigate(-1)}
            className="mx-auto mt-5 flex items-center gap-2 rounded-xl bg-[#4c7a2e] px-5 py-3 text-[12px] font-bold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#375c20]"
          >
            <ArrowLeft size={13} />
            Back to Investments
          </button>
        </div>
        {isWalletModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#26351d]/45 px-4 backdrop-blur-md">
            <div className="w-full max-w-md rounded-[22px] border border-white/80 bg-white p-7 shadow-[0_28px_80px_rgba(55,92,32,0.25)]">
              {/* Header */}
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h2 className="text-[21px] font-bold tracking-[-0.02em] text-[#1f2318]">
                    Enter Wallet Address
                  </h2>

                  <p className="mt-2 text-[13px] leading-6 text-[#6f765f]">
                    Enter the wallet address where you want to receive your
                    tokens.
                  </p>
                </div>

                <button
                  onClick={() => {
                    setIsWalletModalOpen(false);
                    setWalletInput("");
                  }}
                  className="text-xl text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              {/* Wallet Input */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Wallet Address
                </label>

                <input
                  type="text"
                  value={walletInput}
                  onChange={(e) => setWalletInput(e.target.value)}
                  placeholder="Enter wallet address"
                  className="h-[50px] w-full rounded-xl border border-[#d8e5e9] bg-[#faf9f2] px-4 text-[13px] outline-none transition-all focus:border-[#4c7a2e] focus:ring-4 focus:ring-[#4c7a2e]/10"
                />
              </div>

              {/* Info */}
              <div className="mt-5 rounded-2xl border border-[#e5e7dc] bg-[#f7f6ee] p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Tokens</span>
                  <span className="font-semibold text-gray-800">{shares}</span>
                </div>

                <div className="mt-2 flex justify-between text-sm">
                  <span className="text-gray-500">Investment</span>
                  <span className="font-semibold text-gray-800">
                    ${totalInvestment}
                  </span>
                </div>
              </div>

              {/* Buttons */}
              <div className="mt-5 flex gap-3">
                <button
                  onClick={() => {
                    setIsWalletModalOpen(false);
                    setWalletInput("");
                  }}
                  disabled={isSubmitting}
                  className="h-[48px] flex-1 rounded-xl border border-[#d9ddcf] bg-white text-[13px] font-semibold text-[#52666f] transition-all hover:bg-[#f7f6ee]"
                >
                  Cancel
                </button>

                <button
                  onClick={handleConfirmPurchase}
                  disabled={!walletInput.trim() || isSubmitting}
                  className="h-[48px] flex-1 rounded-xl bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-[13px] font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-[#375c20] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? "Processing..." : "Confirm Purchase"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="property-details-page min-h-screen font-['Inter'] text-[#23241c]">
      <style>{`
        .property-details-page {
          --pd-primary: #4c7a2e;
          --pd-ink: #1f2318;
          --pd-muted: #6f765f;
          --pd-ease: cubic-bezier(.22, 1, .36, 1);
          position: relative;
          overflow-x: clip;
          background:
            radial-gradient(circle at 8% 10%, rgba(255,255,255,.96) 0 8%, transparent 27%),
            radial-gradient(circle at 88% 18%, rgba(227,236,214,.86) 0 9%, transparent 29%),
            radial-gradient(circle at 70% 72%, rgba(223,216,188,.46) 0 8%, transparent 27%),
            linear-gradient(135deg, #fdfbf5 0%, #f7f6ee 48%, #eef2e4 100%);
          background-attachment: fixed;
        }


        .property-loading-screen {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          overflow: hidden;
          isolation: isolate;
          background:
            radial-gradient(circle at 15% 20%, rgba(255,255,255,.98) 0 7%, transparent 30%),
            radial-gradient(circle at 86% 22%, rgba(227,236,214,.82) 0 9%, transparent 31%),
            radial-gradient(circle at 72% 78%, rgba(223,216,188,.55) 0 8%, transparent 30%),
            linear-gradient(135deg, #fdfbf5 0%, #f8f7ef 48%, #eef2e4 100%);
        }

        .property-loading-screen::before,
        .property-loading-screen::after {
          content: "";
          position: absolute;
          z-index: -1;
          width: 460px;
          height: 460px;
          border-radius: 9999px;
          pointer-events: none;
          filter: blur(85px);
        }

        .property-loading-screen::before {
          left: -190px;
          top: 8%;
          background: rgba(186, 216, 229, .72);
        }

        .property-loading-screen::after {
          right: -200px;
          bottom: -4%;
          background: rgba(229, 219, 194, .62);
        }

        .property-loading-overlay {
          position: absolute;
          inset: 0;
          background: rgba(255,255,255,.14);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          pointer-events: none;
        }

        .property-loading-content {
          position: relative;
          z-index: 2;
          width: min(92%, 410px);
          display: flex;
          justify-content: center;
        }

        .property-loading-card {
          position: relative;
          width: 100%;
          padding: 34px 30px 30px;
          border: 1px solid rgba(255,255,255,.88);
          border-radius: 28px;
          background: rgba(255,255,255,.78);
          box-shadow:
            0 30px 80px rgba(30, 76, 95, .13),
            inset 0 1px 0 rgba(255,255,255,.95);
          text-align: center;
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          animation: loadingCardIn .65s cubic-bezier(.22,1,.36,1) both;
        }

        .property-loading-icon {
          width: 58px;
          height: 58px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 18px;
          color: #4c7a2e;
          background: linear-gradient(145deg, #f5fbfd, #dcecf2);
          box-shadow:
            0 12px 30px rgba(76,122,46,.12),
            inset 0 1px 0 rgba(255,255,255,.9);
        }

        .property-loading-spinner {
          position: relative;
          width: 52px;
          height: 52px;
          margin: 24px auto 0;
          border: 3px solid rgba(76,122,46,.10);
          border-top-color: #4c7a2e;
          border-right-color: #8a9463;
          border-radius: 9999px;
          animation: propertySpinner 1s linear infinite;
        }

        .property-loading-spinner span {
          position: absolute;
          inset: 9px;
          border-radius: 9999px;
          background: rgba(255,255,255,.78);
          box-shadow: inset 0 0 0 1px rgba(76,122,46,.05);
        }

        .property-loading-dots {
          display: flex;
          justify-content: center;
          gap: 5px;
          margin-top: 18px;
        }

        .property-loading-dots span {
          width: 5px;
          height: 5px;
          border-radius: 9999px;
          background: #4c7a2e;
          opacity: .28;
          animation: loadingDot 1.2s ease-in-out infinite;
        }

        .property-loading-dots span:nth-child(2) {
          animation-delay: .15s;
        }

        .property-loading-dots span:nth-child(3) {
          animation-delay: .3s;
        }

        @keyframes propertySpinner {
          to { transform: rotate(360deg); }
        }

        @keyframes loadingDot {
          0%, 100% { transform: translateY(0); opacity: .25; }
          50% { transform: translateY(-3px); opacity: .9; }
        }

        @keyframes loadingCardIn {
          from {
            opacity: 0;
            transform: translateY(18px) scale(.97);
            filter: blur(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }

        .property-details-page::before,
        .property-details-page::after {
          content: "";
          position: fixed;
          z-index: 0;
          pointer-events: none;
          border-radius: 9999px;
          filter: blur(70px);
          opacity: .72;
        }

        .property-details-page::before {
          width: 430px;
          height: 430px;
          left: -180px;
          top: 130px;
          background: rgba(202, 224, 234, .72);
        }

        .property-details-page::after {
          width: 520px;
          height: 520px;
          right: -230px;
          top: 42%;
          background: rgba(232, 223, 202, .55);
        }

        .property-details-page .reveal-section {
          animation: propertyReveal linear both;
          animation-timeline: view();
          animation-range: entry 0% cover 30%;
        }

        @keyframes propertyReveal {
          from {
            opacity: 0;
            transform: translateY(44px);
            filter: blur(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
            filter: blur(0);
          }
        }

        @supports not (animation-timeline: view()) {
          .property-details-page .reveal-section {
            animation: propertyFallback .7s var(--pd-ease) both;
          }
        }

        @keyframes propertyFallback {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (prefers-reduced-motion: reduce) {
          .property-details-page .reveal-section {
            animation: none !important;
          }

          .property-loading-spinner,
          .property-loading-dots span,
          .property-loading-card {
            animation: none !important;
          }
        }
      `}</style>

      {/* ================= HEADER ================= */}

      <header className="sticky top-0 z-40 border-b border-white/80 bg-white/88 shadow-[0_8px_30px_rgba(76,122,46,0.06)] backdrop-blur-xl">
        <div className="mx-auto flex h-[76px] max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12">
          {/* Logo */}

          <div className="flex items-center gap-2.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-white shadow-[0_8px_22px_rgba(76,122,46,0.20)]">
              <Building2 size={18} strokeWidth={1.7} />
            </div>

            <div>
              <div className="text-[20px] font-bold leading-none tracking-[-0.02em] text-[#23241c]">
                Property
              </div>
            </div>
          </div>

          {/* Back */}

          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 rounded-xl border border-[#d9ddcf] bg-white px-4 py-2.5 text-[12px] font-semibold text-[#49616d] shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[#4c7a2e] hover:text-[#4c7a2e] hover:shadow-md"
          >
            <ArrowLeft size={13} />
            Back to Investments
          </button>
        </div>
      </header>

      {/* ================= MAIN ================= */}

      <main className="relative z-10 mx-auto max-w-[1440px] px-5 py-8 sm:px-8 lg:px-12 lg:py-10">
        {/* Breadcrumb */}

        <div className="mb-7 flex items-center gap-2 text-[11px] font-medium text-[#7b8068]">
          <span>Investments</span>

          <span>/</span>

          <span className="font-semibold text-[#4c7a2e]">{property?.type}</span>
        </div>

        {/* Main Grid */}

        <div className="grid grid-cols-1 gap-7 lg:grid-cols-[1.55fr_1fr] xl:gap-9">
          {/* ================= LEFT ================= */}

          <div>
            {/* Image */}

            <div className="reveal-section relative overflow-hidden rounded-[26px] border border-white/90 bg-white shadow-[0_24px_65px_rgba(76,122,46,0.14)]">
              <div className="relative h-[360px] sm:h-[450px] lg:h-[500px] bg-[#eef0e6]">
                {images.length > 0 ? (
                  <img
                    src={images[selectedImage]}
                    alt={property?.name || "Property"}
                    className="h-full w-full object-cover transition-opacity duration-500"
                    onError={(e) => {
                      console.log("Image failed to load:", e.currentTarget.src);
                    }}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-[#efeee6]">
                    <Building2
                      size={45}
                      strokeWidth={1}
                      className="text-[#9ca5a7]"
                    />
                  </div>
                )}

                {/* Gradient */}

                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

                {/* Category */}

                <div className="absolute left-5 top-5">
                  <span className="rounded-full bg-white/95 px-3.5 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#4c7a2e] shadow-lg backdrop-blur-md">
                    {property?.category}
                  </span>
                </div>

                {/* Status */}

                <div className="absolute right-5 top-5">
                  <span
                    className={`rounded-full px-3.5 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-white shadow-lg ${
                      property?.status === "PUBLISHED"
                        ? "bg-[#4c7a2e]"
                        : "bg-[#375c20]"
                    }`}
                  >
                    {property?.status}
                  </span>
                </div>

                {/* Image navigation */}

                {images.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setSelectedImage(
                          selectedImage === 0
                            ? images.length - 1
                            : selectedImage - 1,
                        )
                      }
                      className="absolute left-5 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-[#4c7a2e] shadow-lg transition-all duration-300 hover:scale-110"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    <button
                      onClick={() =>
                        setSelectedImage(
                          selectedImage === images.length - 1
                            ? 0
                            : selectedImage + 1,
                        )
                      }
                      className="absolute right-5 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-[#4c7a2e] shadow-lg transition-all duration-300 hover:scale-110"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}

              {images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto border-t border-[#e9eadf] bg-white p-4 scrollbar-thin">
                  {images.map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      onClick={() => setSelectedImage(index)}
                      className={`h-[66px] w-[88px] shrink-0 overflow-hidden rounded-xl border-2 bg-[#ebece2] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${
                        selectedImage === index
                          ? "border-[#8a9463]"
                          : "border-transparent"
                      }`}
                    >
                      <img
                        src={image}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ================= HEADING ================= */}

            <div className="reveal-section mt-8">
              <div className="flex items-center gap-2 text-[12px] font-medium text-[#6f765f]">
                <MapPin size={11} />
                {property?.location}
              </div>

              <h1 className="mt-2 text-[34px] font-bold leading-tight tracking-[-0.035em] text-[#1f2318] sm:text-[42px]">
                {property?.name}
              </h1>

              <p className="mt-2 text-[13px] font-semibold uppercase tracking-[0.13em] text-[#6f765f]">
                {property?.type}
              </p>

              <p className="mt-4 max-w-[820px] text-[14px] leading-7 text-[#61747d] sm:text-[15px]">
                {property?.description}
              </p>
            </div>

            {/* ================= PROPERTY INFORMATION ================= */}

            <section className="reveal-section mt-8 rounded-[20px] border border-white bg-white p-6 shadow-[0_18px_48px_rgba(76,122,46,0.08)] sm:p-7">
              <div className="mb-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8a9463]">
                  Property Information
                </p>

                <h2 className="mt-1.5 text-[21px] font-bold tracking-[-0.02em] text-[#2b321f]">
                  About this property
                </h2>
              </div>

              <div className="grid grid-cols-2 gap-x-8 gap-y-7 sm:grid-cols-4">
                {/* Property Type */}

                <InfoItem
                  icon={<Building2 size={14} />}
                  label="Property Type"
                  value={property?.type}
                />

                {/* Area */}

                <InfoItem
                  icon={<Ruler size={14} />}
                  label="Area"
                  value={
                    property?.area
                      ? `${formatNumber(property.area)} sq.ft`
                      : "-"
                  }
                />

                {/* Total Tokens */}

                <InfoItem
                  icon={<Building2 size={14} />}
                  label="Total Tokens"
                  value={formatNumber(property?.totalShares)}
                />

                {/* Tokens Sold */}

                <InfoItem
                  icon={<CheckCircle2 size={14} />}
                  label="Tokens Sold"
                  value={formatNumber(property?.blocksSold)}
                />

                {/* Available */}

                <InfoItem
                  icon={<CheckCircle2 size={14} />}
                  label="Available Tokens"
                  value={formatNumber(property?.availableShares)}
                />

                {/* Submission */}

                <InfoItem
                  icon={<ShieldCheck size={14} />}
                  label="Submission Status"
                  value={
                    property?.submissionStatus
                      ?.replaceAll("_", " ")
                      ?.toUpperCase() || "-"
                  }
                />

                {/* Yield */}

                <InfoItem
                  icon={<TrendingUp size={14} />}
                  label="Annual Yield"
                  value={`${property?.roi || 0}%`}
                />

                {/* Ownership */}

                <InfoItem
                  icon={<Building2 size={14} />}
                  label="Ownership / Token"
                  value={ownershipPerShare}
                />
              </div>
            </section>

            {/* ================= FINANCIALS ================= */}

            <section className="reveal-section mt-5 rounded-[20px] border border-white bg-white p-6 shadow-[0_18px_48px_rgba(76,122,46,0.08)] sm:p-7">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8a9463]">
                Financials
              </p>

              <h2 className="mt-1.5 text-[21px] font-bold tracking-[-0.02em] text-[#2b321f]">
                Investment breakdown
              </h2>

              <div className="mt-4 divide-y divide-[#e6e6dc]">
                <FinancialRow
                  label="Property Value"
                  value={formatCurrency(property?.propertyValue)}
                />

                <FinancialRow
                  label="Price / Token"
                  value={formatCurrency(property?.sharePrice)}
                />

                <FinancialRow
                  label="Gross Annual Yield"
                  value={`${property?.roi || 0}%`}
                />

                <FinancialRow
                  label="Funding"
                  value={`${Number(property?.fundingPercentage || 0).toFixed(
                    1,
                  )}%`}
                />

                <FinancialRow
                  label="Tokens Sold"
                  value={formatNumber(property?.blocksSold)}
                />
              </div>
            </section>

            {/* ================= SECONDARY MARKET ================= */}

            <section className="mt-5 rounded-[20px] border border-white bg-white p-6 shadow-[0_18px_48px_rgba(76,122,46,0.08)] sm:p-7">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8a9463]">
                    Secondary Market
                  </p>
                  <h2 className="mt-1.5 text-[21px] font-bold tracking-[-0.02em] text-[#2b321f]">
                    Resale listings
                  </h2>
                  {property?.vestingDays > 0 && (
                    <p className="mt-1 text-[11px] text-[#8a9463]">
                      Tokens acquired for this property are locked for{" "}
                      {property.vestingDays} days from purchase before they
                      can be resold or transferred.
                    </p>
                  )}
                </div>

                {!myListing && (
                  <button
                    onClick={openListingModal}
                    className="flex h-[38px] items-center gap-1.5 rounded-xl border border-[#4c7a2e] px-4 text-[12px] font-bold text-[#4c7a2e] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#f1f4e9]"
                  >
                    <Plus size={13} />
                    List Tokens
                  </button>
                )}
              </div>

              {myListingError && !myListing && (
                <div className="mt-3 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-2.5">
                  <p className="text-[11px] font-medium text-red-600">
                    Couldn't check if you have a listing — this may be
                    inaccurate.
                  </p>
                  <button
                    onClick={fetchMyListing}
                    className="text-[11px] font-bold text-red-700 underline"
                  >
                    Retry
                  </button>
                </div>
              )}

              {/* Your own listing, if you have one */}
              {myListing && (
                <div className="mt-4 rounded-2xl border border-[#e5e7dc] bg-[#f7f6ee] p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#4c7a2e]">
                      Your Listing
                    </p>
                    {(myListing?.status === "buyback_requested" ||
                      myListing?.buyback_requested) && (
                      <span className="rounded-full bg-[#fbf5e9] px-2.5 py-1 text-[10px] font-bold text-[#8a9463]">
                        Buyback Requested
                      </span>
                    )}
                  </div>

                  <div className="mt-2 flex items-center justify-between text-[13px]">
                    <span className="text-[#6f765f]">
                      {formatNumber(myListing?.blocks ?? myListing?.quantity)}{" "}
                      tokens @{" "}
                      {formatCurrency(
                        myListing?.price_per_token_aed ??
                          myListing?.price_per_block_aed ??
                          myListing?.price_per_token,
                      )}{" "}
                      each
                    </span>
                  </div>

                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => setIsCancelConfirmOpen(true)}
                      className="h-[36px] flex-1 rounded-xl border border-[#d9ddcf] bg-white text-[11px] font-bold text-[#52666f] transition hover:bg-white/70"
                    >
                      Cancel Listing
                    </button>

                    {!(
                      myListing?.status === "buyback_requested" ||
                      myListing?.buyback_requested
                    ) && (
                      <button
                        onClick={handleRequestBuyback}
                        disabled={isRequestingBuyback}
                        className="h-[36px] flex-1 rounded-xl border border-[#4c7a2e] bg-white text-[11px] font-bold text-[#4c7a2e] transition hover:bg-[#f1f4e9] disabled:opacity-50"
                      >
                        {isRequestingBuyback
                          ? "Requesting..."
                          : "Request Buyback"}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* All active listings for this property, excluding your own
                  (shown separately above in "Your Listing") */}
              {(() => {
                const otherListings = listings.filter((listing) => {
                  const seller =
                    listing?.seller_wallet ??
                    listing?.wallet_address ??
                    listing?.seller;
                  return (
                    !walletAddress ||
                    seller?.toLowerCase() !== walletAddress?.toLowerCase()
                  );
                });

                return (
                  <div className="mt-4 divide-y divide-[#e6e6dc]">
                    {isLoadingListings ? (
                      <p className="py-4 text-center text-[12px] text-[#8a9463]">
                        Loading listings...
                      </p>
                    ) : listingsError ? (
                      <div className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                        <p className="text-[11px] font-medium text-red-600">
                          Couldn't load listings — this may not reflect
                          what's actually available.
                        </p>
                        <button
                          onClick={fetchListings}
                          className="text-[11px] font-bold text-red-700 underline"
                        >
                          Retry
                        </button>
                      </div>
                    ) : otherListings.length === 0 ? (
                      <p className="py-4 text-center text-[12px] text-[#8a9463]">
                        No one is reselling tokens for this property yet.
                      </p>
                    ) : (
                      otherListings.map((listing, idx) => {
                      const seller =
                        listing?.seller_wallet ??
                        listing?.wallet_address ??
                        listing?.seller ??
                        "";
                      const blocksAvail =
                        listing?.blocks ?? listing?.quantity ?? 0;
                      const pricePer =
                        listing?.price_per_token_aed ??
                        listing?.price_per_block_aed ??
                        listing?.price_per_token ??
                        0;

                      return (
                        <div
                          key={listing?.id ?? idx}
                          className="flex items-center justify-between gap-3 py-3.5"
                        >
                          <div>
                            <p className="text-[12px] font-semibold text-[#465860]">
                              {seller
                                ? `${seller.slice(0, 4)}...${seller.slice(-4)}`
                                : "Unknown seller"}
                            </p>
                            <p className="mt-0.5 text-[11px] text-[#78898f]">
                              {formatNumber(blocksAvail)} tokens @{" "}
                              {formatCurrency(pricePer)} each
                            </p>
                          </div>

                          <button
                            onClick={() => openBuyListingModal(listing)}
                            className="h-[34px] shrink-0 rounded-xl bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] px-4 text-[11px] font-bold text-white shadow-sm transition-all duration-300 hover:-translate-y-0.5"
                          >
                            Buy
                          </button>
                        </div>
                      );
                    })
                )}
              </div>
                );
              })()}
            </section>
          </div>

          {/* ================= RIGHT ================= */}

          <div className="lg:sticky lg:top-[96px] lg:self-start">
            {/* Investment Card */}

            <div className="reveal-section overflow-hidden rounded-[22px] border border-white bg-white shadow-[0_24px_65px_rgba(76,122,46,0.14)]">
              {/* Total value */}

              <div className="border-b border-[#e6e8dc] px-6 py-7">
                <p className="text-[12px] font-semibold uppercase tracking-[0.10em] text-[#6f765f]">
                  Total Property Value
                </p>

                <div className="mt-2 flex items-end justify-between gap-4">
                  <h2 className="text-[31px] font-bold tracking-[-0.03em] text-[#1f2318]">
                    {formatCurrency(property?.propertyValue)}
                  </h2>

                  <div className="flex items-center gap-1.5 rounded-full bg-[#e3ecd6] px-3 py-1.5 text-[12px] font-bold text-[#4c7a2e]">
                    <TrendingUp size={10} />
                    {property?.roi || 0}% ROI
                  </div>
                </div>
              </div>

              {/* Investment metrics */}

              <div className="grid grid-cols-3 border-b border-[#e6e8dc] text-[15px]">
                <Metric
                  value={formatNumber(property?.totalShares)}
                  label="Total tokens"
                />

                <Metric
                  value={formatCurrency(property?.sharePrice)}
                  label="Price / token"
                />

                <Metric value={ownershipPerShare} label="Ownership" />
              </div>

              {/* Availability */}

              <div className="px-6 py-5">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-medium text-[#6f765f]">
                    Investment availability
                  </p>

                  <p className="text-[11px] font-bold text-[#4c7a2e]">
                    {formatNumber(property?.availableShares)} tokens left
                  </p>
                </div>

                <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#e2e5d8]">
                  <div
                    className="h-full rounded-full bg-[#4c7a2e] transition-[width] duration-700 ease-out"
                    style={{
                      width: `${Math.min(Math.max(fundedPercentage, 0), 100)}%`,
                    }}
                  />
                </div>

                <div className="mt-2 flex justify-between text-[10px] text-[#858a72]">
                  <span>{Math.round(fundedPercentage)}% funded</span>

                  <span>
                    {formatNumber(property?.totalShares)} total tokens
                  </span>
                </div>
              </div>

              {/* Buy section */}

              <div className="border-t border-[#e6e8dc] bg-[#f5f5eb] px-6 py-7">
                <div className="mb-4">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-[#8a9463]">
                    Your Investment
                  </p>

                  <p className="mt-1 text-[9px] leading-relaxed text-[#858e91]">
                    Purchase fractional ownership instead of buying the entire
                    property.
                  </p>
                </div>

                {/* Shares selector */}

                <div className="flex items-center justify-between rounded-[8px] border border-[#e1e3e1] bg-white p-2.5">
                  <div>
                    <p className="text-[12px] font-semibold text-[#60757f]">
                      Number of tokens
                    </p>

                    <p className="mt-1 text-[12px] text-[#87979d]">
                      {formatCurrency(property?.sharePrice)} each
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={decreaseShares}
                      disabled={shares <= 1}
                      className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#d9ddcf] bg-white text-[#4c7a2e] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#4c7a2e] hover:bg-[#f1f4e9] hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <Minus size={12} />
                    </button>

                    <span className="min-w-[34px] text-center text-[17px] font-bold text-[#1f2318]">
                      {shares}
                    </span>

                    <button
                      onClick={increaseShares}
                      disabled={
                        shares >= Number(property?.availableShares || 0)
                      }
                      className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#d9ddcf] bg-white text-[#4c7a2e] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#4c7a2e] hover:bg-[#f1f4e9] hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>

                {/* Total */}

                <div className="mt-6 flex items-center justify-between">
                  <span className="text-[12px] font-medium text-[#687a82]">
                    Total investment
                  </span>

                  <span className="text-[28px] font-bold tracking-[-0.025em] text-[#1f2318]">
                    {formatCurrency(totalInvestment)}
                  </span>
                </div>

                <button
                  // onClick={() => setIsWalletModalOpen(true)}
                  onClick={handleBuyClick}
                  // onClick={() =>
                  //   onBuy?.({
                  //     property: selectedProperty,
                  //     shares,
                  //     totalInvestment,
                  //   })
                  // }
                  disabled={isBuying}
                  className="mt-5 flex h-[54px] w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-[14px] font-bold text-white shadow-[0_14px_30px_rgba(76,122,46,0.22)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#375c20] hover:shadow-[0_18px_36px_rgba(76,122,46,0.27)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isBuying
                    ? "Processing..."
                    : `Buy ${shares} ${shares === 1 ? "Token" : "Tokens"}`}

                  {!isBuying && <ArrowUpRight size={14} />}
                </button>

                <div className="mt-4 flex items-center justify-center gap-2 text-[10px] font-medium text-[#858a72]">
                  <ShieldCheck size={11} />
                  Secure fractional investment
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* ================= WALLET / PURCHASE MODAL ================= */}

      {isWalletModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#26351d]/45 px-4 backdrop-blur-md">
          <div className="w-full max-w-md rounded-[22px] border border-white/80 bg-white p-7 shadow-[0_28px_80px_rgba(55,92,32,0.25)]">
            {/* Header */}
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="text-[21px] font-bold tracking-[-0.02em] text-[#1f2318]">
                  Connect Wallet & Confirm
                </h2>

                <p className="mt-2 text-[13px] leading-6 text-[#6f765f]">
                  Connect your Phantom wallet — you'll approve the payment from
                  this wallet in the next step.
                </p>
              </div>

              <button
                onClick={() => setIsWalletModalOpen(false)}
                className="text-xl text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            {/* Wallet Connect */}
            {!walletAddress ? (
              <button
                onClick={handleConnectWallet}
                disabled={walletConnecting}
                className="flex h-[46px] w-full items-center justify-center rounded-lg border border-[#375c20] bg-white text-sm font-semibold text-[#375c20] transition hover:bg-[#f3f7f8] disabled:opacity-50"
              >
                {walletConnecting ? "Connecting..." : "Connect Phantom Wallet"}
              </button>
            ) : (
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                <div>
                  <p className="text-xs text-gray-500">Connected Wallet</p>

                  <p className="mt-0.5 text-sm font-medium text-[#375c20]">
                    {walletAddress.slice(0, 6)}...
                    {walletAddress.slice(-6)}
                  </p>
                </div>

                <button
                  onClick={handleDisconnectWallet}
                  className="text-xs font-medium text-red-500"
                >
                  Disconnect
                </button>
              </div>
            )}

            {/* Info */}
            <div className="mt-5 rounded-2xl border border-[#e5e7dc] bg-[#f7f6ee] p-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Tokens</span>
                <span className="font-semibold text-gray-800">{shares}</span>
              </div>

              <div className="mt-2 flex justify-between text-sm">
                <span className="text-gray-500">Investment</span>
                <span className="font-semibold text-gray-800">
                  {formatCurrency(totalInvestment)}
                </span>
              </div>
            </div>

            {/* Buttons */}
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setIsWalletModalOpen(false)}
                disabled={isSubmitting || isBuying}
                className="h-[48px] flex-1 rounded-xl border border-[#d9ddcf] bg-white text-[13px] font-semibold text-[#52666f] transition-all hover:bg-[#f7f6ee]"
              >
                Cancel
              </button>

              <button
                onClick={handleConfirmPurchase}
                disabled={!walletAddress || isSubmitting || isBuying}
                className="h-[48px] flex-1 rounded-xl bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-[13px] font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-[#375c20] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting
                  ? "Creating order..."
                  : isBuying
                    ? "Waiting for approval..."
                    : "Confirm Purchase"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= PHANTOM NOT AVAILABLE / REJECTED MODAL ================= */}

      {isPhantomErrorModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#26351d]/45 px-4 backdrop-blur-md">
          <div className="w-full max-w-md rounded-[22px] border border-white/80 bg-white p-7 shadow-[0_28px_80px_rgba(55,92,32,0.25)]">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
              <span className="text-2xl text-red-500">!</span>
            </div>

            <h2 className="text-center text-lg font-semibold text-[#2b351f]">
              Payment / Phantom Issue
            </h2>

            <p className="mt-2 text-center text-sm text-gray-500">
              {phantomErrorMessage}
            </p>

            {purchaseDetails?.id && (
              <div className="mt-4 rounded-lg bg-gray-50 p-3 text-sm">
                <p className="text-gray-500">
                  Your order was created but the payment was not completed. No
                  funds were transferred. You can try approving the payment
                  again.
                </p>

                <div className="mt-2 flex justify-between">
                  <span className="text-gray-500">Order ID</span>
                  <span className="font-semibold text-gray-800">
                    {purchaseDetails?.id}
                  </span>
                </div>

                {walletAddress && (
                  <a
                    href={`https://explorer.solana.com/address/${walletAddress}?cluster=devnet`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
        mt-2
        inline-flex
        items-center
        text-[11px]
        font-semibold
        text-[#4c7a2e]
        transition
        hover:text-[#375c20]
        hover:underline
      "
                  >
                    Check All Transactions on explorer
                  </a>
                )}
              </div>
            )}

            <button
              onClick={() => {
                setIsPhantomErrorModalOpen(false);
                setPhantomErrorMessage("");
              }}
              className="mt-5 h-[44px] w-full rounded-lg bg-[#375c20] text-sm font-semibold text-white transition hover:bg-[#2b351f]"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ================= PURCHASE SUCCESS / TRANSACTION DETAILS MODAL ================= */}

      {isSuccessModalOpen && purchaseDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#26351d]/45 px-4 backdrop-blur-md">
          <div className="w-full max-w-md rounded-[22px] border border-white/80 bg-white p-7 shadow-[0_28px_80px_rgba(55,92,32,0.25)]">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#edf6f1]">
              <CheckCircle2 size={26} className="text-[#4c7a2e]" />
            </div>

            <h2 className="text-center text-lg font-semibold text-[#2b351f]">
              Purchase Successful
            </h2>

            <p className="mt-1 text-center text-sm text-gray-500">
              Your payment was approved in Phantom and confirmed on the Solana
              network.
            </p>

            <div className="mt-5 divide-y divide-[#e6e6dc] rounded-lg border border-[#e6e6dc]">
              <div className="flex items-center justify-between px-3 py-2.5 text-sm">
                <span className="text-gray-500">Tokens Purchased</span>
                <span className="font-semibold text-gray-800">
                  {formatNumber(purchaseDetails.blocks_purchased)}
                </span>
              </div>

              <div className="flex items-center justify-between px-3 py-2.5 text-sm">
                <span className="text-gray-500">Amount Paid</span>
                <span className="font-semibold text-gray-800">
                  {formatCurrency(purchaseDetails?.price_paid_aed?.toFixed(2))}
                </span>
              </div>

              <div className="flex items-center justify-between px-3 py-2.5 text-sm">
                <span className="text-gray-500">Paid From Wallet</span>
                <span className="font-semibold text-gray-800">
                  {walletAddress
                    ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-6)}`
                    : "-"}
                </span>
              </div>

              <div className="px-3 py-2.5 text-sm">
                <span className="text-gray-500">Transaction Signature</span>
                <p className="mt-1 break-all font-semibold text-gray-800">
                  {transactionSignature}
                </p>
                {transactionSignature && (
                  <a
                    href={`https://explorer.solana.com/tx/${transactionSignature}?cluster=devnet`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
        mt-2
        inline-flex
        items-center
        text-[11px]
        font-semibold
        text-[#4c7a2e]
        transition
        hover:text-[#375c20]
        hover:underline
      "
                  >
                    Check this transaction on explorer
                  </a>
                )}
              </div>

              {purchaseDetails.created_at && (
                <div className="flex items-center justify-between px-3 py-2.5 text-sm">
                  <span className="text-gray-500">Date</span>
                  <span className="font-semibold text-gray-800">
                    {new Date(purchaseDetails.created_at).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  setIsSuccessModalOpen(false);
                  setPurchaseDetails(null);
                  setTransactionSignature("");
                }}
                className=" h-[44px] w-full rounded-lg  border
              border-[#e2e4e6]
              bg-white
              text-[#263b47]
              hover:bg-[#fafafa]
              hover:border-[#173d4f]"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setIsSuccessModalOpen(false);
                  setPurchaseDetails(null);
                  setTransactionSignature("");
                  navigate("/customer/portfolio/details", {
                    state: {
                      id: purchaseDetails?.property_id,
                    },
                  });
                }}
                className="h-[44px] rounded-lg bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-sm font-semibold text-white shadow-md shadow-[#4c7a2e]/20 transition hover:-translate-y-0.5"
              >
                View Investment
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ================= CREATE LISTING MODAL ================= */}

      {isListingModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#26351d]/45 px-4 backdrop-blur-md">
          <div className="w-full max-w-md rounded-[22px] border border-white/80 bg-white p-7 shadow-[0_28px_80px_rgba(55,92,32,0.25)]">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="text-[21px] font-bold tracking-[-0.02em] text-[#1f2318]">
                  List Tokens For Sale
                </h2>
                <p className="mt-2 text-[13px] leading-6 text-[#6f765f]">
                  Set how many tokens to list and your price per token —
                  you'll approve this from your Phantom wallet.
                </p>
                {property?.vestingDays > 0 && (
                  <p className="mt-2 text-[12px] font-medium text-[#8a9463]">
                    Note: tokens acquired within the last {property.vestingDays}{" "}
                    days may still be locked and cannot be listed yet.
                  </p>
                )}
              </div>
              <button
                onClick={() => setIsListingModalOpen(false)}
                className="text-xl text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-[11px] font-semibold text-[#6f765f]">
                  Tokens to list
                </label>
                <input
                  type="number"
                  min={1}
                  value={listingBlocks}
                  onChange={(e) => setListingBlocks(e.target.value)}
                  className="mt-1.5 h-[46px] w-full rounded-xl border border-[#d9ddcf] px-3 text-sm outline-none focus:border-[#4c7a2e]"
                />
              </div>

              <div>
                <label className="text-[11px] font-semibold text-[#6f765f]">
                  Price per token (AED)
                </label>
                <input
                  type="number"
                  min={1}
                  value={listingPrice}
                  onChange={(e) => setListingPrice(e.target.value)}
                  className="mt-1.5 h-[46px] w-full rounded-xl border border-[#d9ddcf] px-3 text-sm outline-none focus:border-[#4c7a2e]"
                />
              </div>

              {listingError && (
                <p className="text-[12px] font-medium text-red-500">
                  {listingError}
                </p>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setIsListingModalOpen(false)}
                disabled={isCreatingListing}
                className="h-[48px] flex-1 rounded-xl border border-[#d9ddcf] bg-white text-[13px] font-semibold text-[#52666f] transition-all hover:bg-[#f7f6ee]"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateListing}
                disabled={isCreatingListing}
                className="h-[48px] flex-1 rounded-xl bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-[13px] font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isCreatingListing ? "Listing..." : "Create Listing"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= CANCEL LISTING CONFIRM MODAL ================= */}

      {isCancelConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#26351d]/45 px-4 backdrop-blur-md">
          <div className="w-full max-w-md rounded-[22px] border border-white/80 bg-white p-7 shadow-[0_28px_80px_rgba(55,92,32,0.25)]">
            <h2 className="text-[19px] font-bold text-[#1f2318]">
              Cancel this listing?
            </h2>
            <p className="mt-2 text-[13px] leading-6 text-[#6f765f]">
              Your tokens stay in your wallet — this just removes the listing
              from the marketplace. You'll approve this from Phantom.
            </p>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setIsCancelConfirmOpen(false)}
                disabled={isCancellingListing}
                className="h-[48px] flex-1 rounded-xl border border-[#d9ddcf] bg-white text-[13px] font-semibold text-[#52666f] transition-all hover:bg-[#f7f6ee]"
              >
                Keep Listing
              </button>
              <button
                onClick={handleCancelListing}
                disabled={isCancellingListing}
                className="h-[48px] flex-1 rounded-xl bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-[13px] font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isCancellingListing ? "Cancelling..." : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= BUY FROM LISTING MODAL ================= */}

      {isBuyListingModalOpen && selectedListing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#26351d]/45 px-4 backdrop-blur-md">
          <div className="w-full max-w-md rounded-[22px] border border-white/80 bg-white p-7 shadow-[0_28px_80px_rgba(55,92,32,0.25)]">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-[21px] font-bold tracking-[-0.02em] text-[#1f2318]">
                Buy From Listing
              </h2>
              <button
                onClick={() => {
                  setIsBuyListingModalOpen(false);
                  setSelectedListing(null);
                }}
                className="text-xl text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="rounded-2xl border border-[#e5e7dc] bg-[#f7f6ee] p-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Available</span>
                <span className="font-semibold text-gray-800">
                  {formatNumber(
                    selectedListing?.blocks ?? selectedListing?.quantity,
                  )}{" "}
                  tokens
                </span>
              </div>
              <div className="mt-2 flex justify-between text-sm">
                <span className="text-gray-500">Price / token</span>
                <span className="font-semibold text-gray-800">
                  {formatCurrency(
                    selectedListing?.price_per_token_aed ??
                      selectedListing?.price_per_block_aed ??
                      selectedListing?.price_per_token,
                  )}
                </span>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-[11px] font-semibold text-[#6f765f]">
                Tokens to buy
              </label>
              <div className="mt-1.5 flex items-center justify-between rounded-xl border border-[#d9ddcf] bg-white p-2.5">
                <button
                  onClick={() =>
                    setBuyListingBlocks((prev) => Math.max(1, prev - 1))
                  }
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#d9ddcf] text-[#4c7a2e]"
                >
                  <Minus size={12} />
                </button>
                <span className="text-[15px] font-bold text-[#1f2318]">
                  {buyListingBlocks}
                </span>
                <button
                  onClick={() =>
                    setBuyListingBlocks((prev) =>
                      Math.min(
                        Number(
                          selectedListing?.blocks ??
                            selectedListing?.quantity ??
                            1,
                        ),
                        prev + 1,
                      ),
                    )
                  }
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#d9ddcf] text-[#4c7a2e]"
                >
                  <Plus size={12} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-[12px] font-medium text-[#687a82]">
                Total
              </span>
              <span className="text-[22px] font-bold text-[#1f2318]">
                {formatCurrency(
                  Number(buyListingBlocks) *
                    Number(
                      selectedListing?.price_per_token_aed ??
                        selectedListing?.price_per_block_aed ??
                        selectedListing?.price_per_token ??
                        0,
                    ),
                )}
              </span>
            </div>

            <button
              onClick={handleBuyFromListing}
              disabled={isBuyingListing}
              className="mt-5 h-[50px] w-full rounded-xl bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-[13px] font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isBuyingListing ? "Processing..." : "Confirm Purchase"}
            </button>
          </div>
        </div>
      )}

      <ProfileCompletionModal
        isOpen={isProfileModalOpen}
        onClose={() => {
          if (!isProfileUpdating) {
            setIsProfileModalOpen(false);
            setProfileError("");
          }
        }}
        profile={profile}
        onSubmit={updateInvestorProfile}
        isSubmitting={isProfileUpdating}
        error={profileError}
      />
      <PaymentMethodModal
  isOpen={isPaymentMethodModalOpen}
  onClose={() => setIsPaymentMethodModalOpen(false)}
  amount={formatCurrency(totalInvestment)}

  onSelectPaymentMethod={(method) => {
    setIsPaymentMethodModalOpen(false);

    if (method?.id === "phantom") {
      const savedWalletAddress =
        profile?.wallet_address || profile?.walletAddress || "";

      if (!savedWalletAddress.trim()) {
        setProfileError("");
        setIsProfileModalOpen(true);
        return;
      }

      setWalletInput(savedWalletAddress);
      setIsWalletModalOpen(true);
      return;
    }

    if (method?.id === "debit_card") {
      // Card payment flow
      console.log("Debit Card selected");
      return;
    }

    if (method?.id === "credit_card") {
      // Card payment flow
      console.log("Credit Card selected");
      return;
    }

    if (method?.id === "bank_transfer") {
      // Bank transfer flow
      console.log("Bank Transfer selected");
      return;
    }
  }}
/>
    </div>
  );
};

const Metric = ({ value, label }) => {
  return (
    <div className="px-3 py-5 text-center transition-colors duration-300 hover:bg-[#f5f5eb]">
      <p className="text-[17px] font-bold text-[#4c7a2e]">{value}</p>

      <p className="mt-1 text-[11px] font-medium text-[#858a72]">{label}</p>
    </div>
  );
};

const InfoItem = ({ icon, label, value }) => {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#fbf5e9] text-[#8a9463]">
        {icon}
      </div>

      <div>
        <p className="text-[11px] font-medium text-[#8a9a9f]">{label}</p>

        <p className="mt-1 text-[13px] font-bold text-[#304650]">{value}</p>
      </div>
    </div>
  );
};

const FinancialRow = ({ label, value }) => {
  return (
    <div className="flex items-center justify-between py-3.5">
      <span className="text-[12px] font-medium text-[#78898f]">{label}</span>

      <span className="text-[13px] font-semibold text-[#465860]">{value}</span>
    </div>
  );
};
const ProfileCompletionModal = ({
  isOpen,
  onClose,
  profile,
  onSubmit,
  isSubmitting,
  error,
}) => {
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    wallet_address: "",
  });

  const [emiratesId, setEmiratesId] = useState(null);

  useEffect(() => {
    if (!profile || !isOpen) return;

    setFormData({
      first_name: profile?.first_name || "",
      last_name: profile?.last_name || "",
      phone_number: profile?.phone_number || profile?.phone || "",
      wallet_address: profile?.wallet_address || profile?.walletAddress || "",
    });

    setEmiratesId(null);
  }, [profile, isOpen]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;

    let digits = value.replace(/\D/g, "");

    if (digits.startsWith("971")) {
      digits = digits.slice(3);
    }

    digits = digits.slice(0, 9);

    let formatted = "+971";

    if (digits.length > 0) {
      formatted += ` ${digits.slice(0, 2)}`;
    }

    if (digits.length > 2) {
      formatted += ` ${digits.slice(2, 5)}`;
    }

    if (digits.length > 5) {
      formatted += ` ${digits.slice(5, 9)}`;
    }

    handleChange("phone_number", formatted);
  };

  const handleEmiratesIdChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      alert("Please upload a PDF, JPG or PNG file.");
      e.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert("Emirates ID file size must be less than 10MB.");
      e.target.value = "";
      return;
    }

    setEmiratesId(file);
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   if (!formData.wallet_address.trim()) {
  //     return;
  //   }

  //   const payload = new FormData();

  //   // Optional fields
  //   if (formData.first_name.trim()) {
  //     payload.append("first_name", formData.first_name.trim());
  //   }

  //   if (formData.last_name.trim()) {
  //     payload.append("last_name", formData.last_name.trim());
  //   }

  //   if (formData.phone_number.trim()) {
  //     payload.append("phone_number", formData.phone_number.trim());
  //   }

  //   // REQUIRED
  //   payload.append("wallet_address", formData.wallet_address.trim());

  //   // Optional
  //   if (emiratesId) {
  //     payload.append("emirates_id", emiratesId);
  //   }

  //   await onSubmit(payload);
  // };


  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!formData.first_name.trim()) {
    message.error("First Name is Required")
    return;
  }

  if (!formData.last_name.trim()) {
     message.error("Last Name is Required")
    return;
  }

  // if (!formData.phone_number.trim()) {
  //   //  message.error("Phone Number is Required")
  //   return;
  // }

  if (!formData.wallet_address.trim()) {
     message.error("Wallet is Required")
    return;
  }

  // if (!emiratesId) {
  //   //  message.error("Emirates is Required")
  //   return;
  // }

  const payload = new FormData();

  payload.append("first_name", formData.first_name.trim());
  payload.append("last_name", formData.last_name.trim());
  if(formData.phone_number) {
    payload.append("phone_number", formData.phone_number.trim());
  }
  payload.append(
    "wallet_address",
    formData.wallet_address.trim()
  );
  if(emiratesId){
    payload.append("emirates_id", emiratesId);
  }

  await onSubmit(payload);
};

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#26351d]/45 px-4 backdrop-blur-md">
      <div
        className="
          w-full
          max-w-[560px]
          max-h-[90vh]
          overflow-hidden
          rounded-[24px]
          border
          border-white/80
          bg-white
          shadow-[0_30px_90px_rgba(55,92,32,0.22)]
        "
      >
        {/* HEADER */}
        <div className="flex items-center justify-between border-b border-[#e7e9df] px-6 py-5">
          <div>
            <h2 className="text-[20px] font-bold text-[#1f2318]">
              Complete Your Profile
            </h2>

            <p className="mt-1.5 text-[11px] leading-5 text-[#7a806d]">
              Please add your wallet address before purchasing tokens.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="
              flex
              h-8
              w-8
              items-center
              justify-center
              rounded-full
              bg-[#f3f4ee]
              text-[#6f765f]
              transition
              hover:bg-[#e6e9df]
            "
          >
            ×
          </button>
        </div>

        {/* CONTENT */}
        <form
          onSubmit={handleSubmit}
          className="max-h-[calc(90vh-90px)] overflow-y-auto p-6"
        >
          <div className="mb-5 rounded-[14px] border border-[#e4eadb] bg-[#f7faf3] px-4 py-3.5">
            <p className="text-[10px] font-semibold text-[#4c7a2e]">
              Wallet address required
            </p>

            <p className="mt-1 text-[9px] leading-4 text-[#7b8470]">
              A wallet address is required to continue with your token purchase.
              Other profile information is optional.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* FIRST NAME */}
            <div>
              <label className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
                First Name
              </label>

              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => handleChange("first_name", e.target.value)}
                placeholder="Enter first name"
                className="
                  h-[44px]
                  w-full
                  rounded-[10px]
                  border
                  border-[#dfe4dd]
                  bg-white
                  px-3
                  text-[11px]
                  text-[#243d49]
                  outline-none
                  transition
                  placeholder:text-[#a5aaa3]
                  focus:border-[#7da458]
                  focus:ring-2
                  focus:ring-[#7da458]/10
                "
              />
            </div>

            {/* LAST NAME */}
            <div>
              <label className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
                Last Name
              </label>

              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => handleChange("last_name", e.target.value)}
                placeholder="Enter last name"
                className="
                  h-[44px]
                  w-full
                  rounded-[10px]
                  border
                  border-[#dfe4dd]
                  bg-white
                  px-3
                  text-[11px]
                  text-[#243d49]
                  outline-none
                  transition
                  placeholder:text-[#a5aaa3]
                  focus:border-[#7da458]
                  focus:ring-2
                  focus:ring-[#7da458]/10
                "
              />
            </div>

            {/* PHONE */}
            <div>
              <label className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
                Phone Number
              </label>

              <input
                type="tel"
                value={formData.phone_number}
                onChange={handlePhoneChange}
                placeholder="+971 50 000 0000"
                className="
                  h-[44px]
                  w-full
                  rounded-[10px]
                  border
                  border-[#dfe4dd]
                  bg-white
                  px-3
                  text-[11px]
                  text-[#243d49]
                  outline-none
                  transition
                  placeholder:text-[#a5aaa3]
                  focus:border-[#7da458]
                  focus:ring-2
                  focus:ring-[#7da458]/10
                "
              />
            </div>

            {/* WALLET */}
            <div>
              <label className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
                Wallet Address
                <span className="ml-1 text-[#4c7a2e]">*</span>
              </label>

              <input
                type="text"
                value={formData.wallet_address}
                onChange={(e) => handleChange("wallet_address", e.target.value)}
                placeholder="Enter wallet address"
                required
                className="
                  h-[44px]
                  w-full
                  rounded-[10px]
                  border
                  border-[#dfe4dd]
                  bg-white
                  px-3
                  text-[11px]
                  text-[#243d49]
                  outline-none
                  transition
                  placeholder:text-[#a5aaa3]
                  focus:border-[#7da458]
                  focus:ring-2
                  focus:ring-[#7da458]/10
                "
              />
            </div>
          </div>

          {/* EMIRATES ID */}
          <div className="mt-4">
            <label className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
              Emirates ID
            </label>

            {!emiratesId ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="
                  flex
                  min-h-[90px]
                  w-full
                  flex-col
                  items-center
                  justify-center
                  rounded-[12px]
                  border
                  border-dashed
                  border-[#cfd9c8]
                  bg-[#fafcf8]
                  px-4
                  text-center
                  transition
                  hover:border-[#7da458]
                  hover:bg-[#f6faf2]
                "
              >
                <p className="text-[10px] font-semibold text-[#596257]">
                  Upload Emirates ID
                </p>

                <p className="mt-1 text-[8px] text-[#9a9f96]">
                  PDF, JPG or PNG · Max 10MB · Optional
                </p>
              </button>
            ) : (
              <div className="flex items-center justify-between rounded-[12px] border border-[#dce7d5] bg-[#f7faf4] px-3.5 py-3">
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold text-[#37452f]">
                    {emiratesId.name}
                  </p>

                  <p className="mt-0.5 text-[8px] text-[#92968e]">
                    {(emiratesId.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setEmiratesId(null);

                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  className="ml-3 text-[#8c9388] hover:text-[#b45850]"
                >
                  ×
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={handleEmiratesIdChange}
            />
          </div>

          {error && (
            <div className="mt-4 rounded-[10px] border border-red-200 bg-red-50 px-3 py-2.5">
              <p className="text-[9px] font-medium text-red-600">{error}</p>
            </div>
          )}

          {/* BUTTONS */}
          <div className="mt-6 flex justify-end gap-3 border-t border-[#ebe9e2] pt-5">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="
                h-[42px]
                rounded-[10px]
                border
                border-[#d9ddd8]
                bg-[#f8f8f5]
                px-5
                text-[10px]
                font-semibold
                text-[#59656a]
                transition
                hover:bg-[#f1f2ee]
              "
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting || !formData.wallet_address.trim()}
              className="
                h-[42px]
                rounded-[10px]
                bg-[#4c7a2e]
                px-5
                text-[10px]
                font-semibold
                text-white
                shadow-[0_6px_18px_rgba(76,122,46,0.16)]
                transition
                hover:bg-[#375c20]
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >
              {isSubmitting ? "Updating..." : "Save & Continue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyInvestmentDetails;